import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Spinner, Alert, Badge, Table, Input, Button, Row, Col, Card, CardBody } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { useApiClient } from '../../api/client.jsx';

const fmt = new Intl.NumberFormat('de-DE');

function Kpi({ label, value, unit }) {
  return (
    <Col xs="6" md="3" lg="2">
      <Card className="h-100 border-0 shadow-sm">
        <CardBody className="py-3">
          <div className="text-muted small text-uppercase">{label}</div>
          <div className="fs-4 fw-semibold">{value}<span className="fs-6 text-muted"> {unit}</span></div>
        </CardBody>
      </Card>
    </Col>
  );
}

function FleetKpis() {
  const { fetch } = useApiClient();
  const { data } = useQuery({
    queryKey: ['fleet-stats'],
    queryFn: () => fetch('/v1/hosting-ops/enhance/fleet-stats'),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });
  const k = data?.k3s || {};
  const e = data?.enhance || {};
  return (
    <Row className="g-3 mb-4">
      <Kpi label="CPU-Kerne (k3s)" value={fmt.format(k.cpuCores ?? 0)} unit="Kerne" />
      <Kpi label="RAM (k3s)" value={fmt.format(k.ramGb ?? 0)} unit="GB" />
      <Kpi label="Storage (k3s)" value={fmt.format(k.storageGb ?? 0)} unit="GB" />
      <Kpi label="k3s-Container" value={fmt.format(k.containers ?? 0)} unit={`in ${fmt.format(k.pods ?? 0)} Pods`} />
      <Kpi label="Nodes / Cluster" value={`${fmt.format(k.nodes ?? 0)} / ${fmt.format(k.clusters ?? 0)}`} unit="" />
      <Kpi label="Enhance-Websites" value={e.websites == null ? '—' : fmt.format(e.websites)} unit={`· ${fmt.format(e.servers ?? 0)} Server`} />
    </Row>
  );
}

/**
 * #263 Kapazitätsbewusstes Enhance-Placement — Betreiber-Sicht.
 *
 * Pro Enhance-Server: Klasse (performance/standard), Überbuchungs-Ratio
 * (pro Server editierbar), verkaufte Menge vs. Kapazität, gemessene Auslastung
 * (Zabbix), Wachstum/Tag und geschätztes „voll in X Tagen". Alert-Badge, wenn
 * ein Server volläuft oder bald neue Server nötig sind.
 * Daten: orbit-backend /v1/hosting-ops/enhance/servers → Nexus.
 */
const pct = (v) => (v == null ? '—' : `${Math.round(Number(v))} %`);

export function ServerCapacityPage() {
  const { fetch } = useApiClient();
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['enhance-servers'],
    queryFn: () => fetch('/v1/hosting-ops/enhance/servers'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['enhance-servers'] });
  const patch = useMutation({
    mutationFn: ({ serverId, body }) => fetch(`/v1/hosting-ops/enhance/servers/${serverId}`, { method: 'PATCH', body }),
    onSuccess: () => { invalidate(); },
    onError: (e) => toast.error(e?.message || 'Speichern fehlgeschlagen'),
  });
  const sync = useMutation({
    mutationFn: () => fetch('/v1/hosting-ops/enhance/servers/sync', { method: 'POST' }),
    onSuccess: (r) => { toast.success(`Server synchronisiert (${r?.synced ?? 0})`); invalidate(); },
    onError: (e) => toast.error(e?.message || 'Sync fehlgeschlagen'),
  });

  const servers = data?.servers || [];
  const attention = servers.filter((s) => s.needsAttention);
  const set = (serverId, body) => patch.mutate({ serverId, body });

  return (
    <div>
      <PageHeader
        title="Server-Kapazität"
        subtitle="Enhance-Placement: Überbuchung, Auslastung & Wachstums-Prognose"
        actions={<Button size="sm" color="primary" outline disabled={sync.isPending} onClick={() => sync.mutate()}>
          {sync.isPending ? 'Sync…' : 'Server synchronisieren'}
        </Button>}
      />

      <FleetKpis />

      {isLoading && <Alert color="info" className="d-flex align-items-center gap-2 small"><Spinner size="sm" /> Server laden…</Alert>}
      {isError && !isLoading && <Alert color="warning" className="small">Server konnten nicht geladen werden (Nexus/Enhance nicht erreichbar?).</Alert>}

      {attention.length > 0 && (
        <Alert color="danger" className="small">
          <b>{attention.length} Server</b> brauchen Aufmerksamkeit — hohe Auslastung oder bald voll:
          {' '}{attention.map((s) => s.hostname).join(', ')}. Zeit, neue Server bereitzustellen.
        </Alert>
      )}

      {!isLoading && servers.length === 0 && !isError && (
        <Alert color="light" className="small">Kein Server im Inventar. Klick „Server synchronisieren", um sie aus Enhance zu laden.</Alert>
      )}

      {servers.length > 0 && (
        <Table size="sm" responsive hover>
          <thead>
            <tr>
              <th>Server</th><th>Klasse</th><th>Ratio</th><th>Kapazität</th><th>Belegt</th>
              <th>Auslastung</th><th>Wachstum/Tag</th><th>Voll in</th><th>Aktiv</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {servers.map((s) => (
              <tr key={s.serverId} className={s.needsAttention ? 'table-danger' : undefined}>
                <td className="small">{s.hostname}</td>
                <td>
                  <Input type="select" bsSize="sm" value={s.tierClass} style={{ width: 130 }}
                    onChange={(e) => set(s.serverId, { tierClass: e.target.value })}>
                    <option value="performance">performance</option>
                    <option value="standard">standard</option>
                  </Input>
                </td>
                <td>
                  {s.tierClass === 'performance' ? (
                    <Input type="select" bsSize="sm" value={String(s.ratio)} style={{ width: 90 }}
                      onChange={(e) => set(s.serverId, { oversubscriptionRatio: Number(e.target.value) })}>
                      <option value="1">1:1</option>
                      <option value="1.5">1:1.5</option>
                      <option value="2">1:2</option>
                    </Input>
                  ) : <span className="text-muted small">auto</span>}
                </td>
                <td className="small">
                  <Input type="number" bsSize="sm" min={0} defaultValue={s.slotCapacity} style={{ width: 80 }}
                    onBlur={(e) => { const v = Number(e.target.value); if (v !== s.slotCapacity) set(s.serverId, { slotCapacity: v }); }} />
                </td>
                <td className="small">{s.capacity != null ? `${s.sold} / ${s.capacity}` : s.sold}{s.soldPct != null ? ` (${s.soldPct}%)` : ''}</td>
                <td className="small">{pct(s.utilization)}</td>
                <td className="small">{s.growthPerDay == null ? '—' : `${s.growthPerDay > 0 ? '+' : ''}${s.growthPerDay} %`}</td>
                <td className="small">{s.daysUntilFull == null ? '—' : `${s.daysUntilFull} T`}</td>
                <td>
                  <Input type="switch" checked={!!s.enabled} onChange={(e) => set(s.serverId, { enabled: e.target.checked })} />
                </td>
                <td>{s.needsAttention ? <Badge color="danger">Achtung</Badge> : <Badge color="success">OK</Badge>}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <p className="text-muted small mt-2">
        Performance-Server werden nur bis zur Ratio belegt (max. 1:2). Standard-Server erhalten neue Kunden
        automatisch auf dem Server mit der geringsten gemessenen Last. Auslastung/Prognose aus Zabbix
        (nächtlicher Poll).
      </p>
    </div>
  );
}
